import Supercluster from "supercluster"; // Кластеризация
import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import Map, { Marker } from "react-map-gl";

import "mapbox-gl/dist/mapbox-gl.css";
import "./MapSection.css";
import "./MapBoxOverride.css"; // Импортируем переопределяющие стили для MapBox

import { GraphChart } from "./FullscreenChart";
import FullscreenChart from "./FullscreenChart";
import API_URL from "./config";

import IconRazv from "../assets/img/icon razv.png"; 
import IconSver from "../assets/img/icon sver.png"; 
import PinGreen from "../assets/img/location-pin  green.png"; 
import PinYellow from "../assets/img/location-pin yellow.png";
import PinOrange from "../assets/img/location-pin orange.png"; 
import PinRed from "../assets/img/location-pin red.png";
import PinBlack from "../assets/img/location-pin black.png";
import PinTurquoise from "../assets/img/location-pin turquoise.png";

const locations = {
  norilsk: { latitude: 69.343985, longitude: 88.210393 },
  talnakh: { latitude: 69.491663, longitude: 88.390416 },
  kaierkan: { latitude: 69.351427, longitude: 87.753698 },
  oganer: { latitude: 69.358857, longitude: 88.372239 }
};

const MAPBOX_TOKEN = "pk.eyJ1IjoiaWdvcnNlcmdlZXZpY2hpc2l0IiwiYSI6ImNtNm5rNDAzaTB1OTkyaXM0aTZ5ZzAzbzcifQ.rUgmEzQPE3BUs_PidEQj6w";

// Оптимизированная функция получения иконки маркера с мемоизацией
const getMemoizedMarkerIcon = (house, selectedId) => {
  if (!house || !house.properties) return PinGreen; // Если данных нет, метка зелёная
  if (selectedId === house.properties.id) return PinTurquoise; 
  if (house.properties.is_demolished) return PinBlack;
  if (house.properties.series === 'Индивидуальный проект ("сталинка")') return PinOrange;
  if (house.properties.is_emergency === "Да") return PinRed;
  if (house.properties.wear_percent > 50) return PinYellow;
  return PinGreen;
};

// Запасной список серий домов на случай недоступности API
const FALLBACK_SERIES = [
  "111-84М",
  "9КП-36",
  "Индивидуальный проект (\"сталинка\")",
  "111-84",
  "К-84",
  "К-69, 12 этажей",
  "1-447",
  "К-69",
  "111-142",
  "НК-12",
  "1-464М",
  "1-447-С",
  "111-92Н",
  "111-84, КОПЭН",
  "1-464-Д-82",
  "1-447М",
  "111-112",
  "1-335А",
  "1-464",
  "К-69, 5 этажей",
  "111-112 - Основа, К-69 - Вставка"
];

function MapSection({ houses, onSelectBuilding, onSeriesChange, selectedBuildingDetails, userRole }) {
  const mapContainerRef = useRef(null);
  const infoPanelRef = useRef(null);  // Реф для инфо-панели
  const [zoom, setZoom] = useState(8);
  const [viewport, setViewport] = useState({
    latitude: locations.norilsk.latitude,
    longitude: locations.norilsk.longitude,
    zoom: 8 // Уменьшаем начальный уровень зума для лучшей кластеризации
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedHouse, setSelectedHouse] = useState(null);
  const [selectedHouseId, setSelectedHouseId] = useState(null);
  const [legendExpanded, setLegendExpanded] = useState(false);
  const [fullscreenChartVisible, setFullscreenChartVisible] = useState(false);
  const [fullscreenMoralChartVisible, setFullscreenMoralChartVisible] = useState(false);
  const [viewBounds, setViewBounds] = useState(null); // Добавляем состояние для хранения видимых границ
  const [currentDistrictName, setCurrentDistrictName] = useState(null);

  const [selectedLocation, setSelectedLocation] = useState('norilsk'); // Добавляем состояние для выбранного района
  const [selectedSeries, setSelectedSeries] = useState(['Все серии']); // Теперь это массив выбранных серий
  const [buildingSeries, setBuildingSeries] = useState([]); // Добавляем состояние для хранения серий зданий
  const [isLoadingSeries, setIsLoadingSeries] = useState(true); // Индикатор загрузки серий
  const [seriesError, setSeriesError] = useState(null); // Ошибка загрузки серий
  const [showSeriesList, setShowSeriesList] = useState(false); // Состояние для отображения списка серий
  const [seriesListClosing, setSeriesListClosing] = useState(false); // Состояние для анимации закрытия списка
  const seriesListRef = useRef(null); // Для отслеживания клика вне списка
  const seriesContainerRef = useRef(null); // Новый ref для контейнера фильтра серий
  const [isControlsExpanded, setIsControlsExpanded] = useState(true); // Новое состояние
  const [dropdownPosition, setDropdownPosition] = useState({ top: 100, left: 10 }); // Позиция выпадающего списка


  // Обновим состояние для двух возможных позиций панели вместо трех
  const [infoPanelState, setInfoPanelState] = useState('visible'); // 'visible', 'hidden'
  
  // Для обработки свайпа
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [touchStartElement, setTouchStartElement] = useState(null);
  
  // Минимальное расстояние для свайпа (в пикселях)
  const minSwipeDistance = 50;
  
  // Обновим обработчики сенсорных событий для свайпа вниз для закрытия
  const onTouchStart = (e) => {
    // Сохраняем не только позицию касания, но и элемент, с которого начался свайп
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
    
    // Определяем, был ли свайп начат в заголовке панели
    let element = e.target;
    let isHeader = false;
    
    // Проверяем, находится ли элемент или его родители в заголовке панели
    while (element && !isHeader) {
      if (element.id === 'info-header') {
        isHeader = true;
      }
      element = element.parentElement;
    }
    
    setTouchStartElement(isHeader ? 'header' : 'content');
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    // Вычисляем расстояние свайпа
    const distance = touchEnd - touchStart;
    
    // Свайп вниз для закрытия панели только если начали свайп в заголовке
    if (distance > minSwipeDistance && window.innerWidth <= 768 && touchStartElement === 'header') {
      closeInfoPanel();
    }
    
    // Сбрасываем состояние
    setTouchStart(null);
    setTouchEnd(null);
    setTouchStartElement(null);
  };

  // Функция для закрытия выпадающего списка с анимацией
  const closeSeriesList = () => {
    setSeriesListClosing(true);
    
    // Небольшая задержка перед фактическим закрытием для анимации
    setTimeout(() => {
      setShowSeriesList(false);
      setSeriesListClosing(false);
    }, 200); // Длительность совпадает с CSS анимацией
  };

  // Обработчик клика вне списка серий для его закрытия
  useEffect(() => {
    function handleClickOutside(event) {
      // Проверяем, был ли клик вне списка и вне кнопки
      if (
        seriesListRef.current && 
        !seriesListRef.current.contains(event.target) &&
        seriesContainerRef.current &&
        !seriesContainerRef.current.contains(event.target)
      ) {
        closeSeriesList();
      }
    }

    // Добавляем обработчик при открытии списка
    if (showSeriesList) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    
    // Удаляем обработчик при закрытии списка или размонтировании
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showSeriesList]);

  // Функция загрузки серий зданий с API
  const fetchBuildingSeries = async () => {
    setIsLoadingSeries(true);
    setSeriesError(null);
    try {
      // Проверяем наличие токена в localStorage
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${API_URL}/House/building-series`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Добавляем заголовок авторизации, если есть токен
          ...token ? { 'Authorization': `Bearer ${token}` } : {}
        },
        credentials: 'include' // Включаем передачу куки с запросом
      });
      
      if (response.status === 401) {
        // Если требуется авторизация, используем запасной список
        setBuildingSeries(['Все серии', ...FALLBACK_SERIES]);
        return;
      } else if (!response.ok) {
        throw new Error(`Не удалось загрузить серии зданий: ${response.statusText}`);
      }
      
      const data = await response.json();
      // Добавляем опцию "Все серии" в начало списка
      setBuildingSeries(['Все серии', ...data]);
          } catch (error) {
        // Ошибка при загрузке серий зданий
      // В случае ошибки используем запасной список
      setBuildingSeries(['Все серии', ...FALLBACK_SERIES]);
    } finally {
      setIsLoadingSeries(false);
    }
  };

  // Загружаем серии зданий при монтировании компонента
  useEffect(() => {
    fetchBuildingSeries();
  }, []);

  const toggleLegend = () => {
    setLegendExpanded(!legendExpanded);
  };

  // Мемоизация функции выбора цвета метки
  const getMarkerIcon = useCallback((house, selectedId) => {
    return getMemoizedMarkerIcon(house, selectedId);
  }, []);

  const handleFullScreen = () => {
    // Улучшенная проверка для iOS устройств
    const isIOS = (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) || // iPad на iOS 13+
                  /iPhone|iPad/.test(navigator.platform);
    
    // Дополнительная проверка для мобильных устройств
    const isMobile = (window.innerWidth <= 768) || 
                     /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isIOS || isMobile) {
      // На iOS используем альтернативный подход: увеличиваем карту вместо полноэкранного режима
      if (!isFullscreen) {
        // Запоминаем текущий размер и положение карты
        const mapContainer = mapContainerRef.current;
        const originalStyles = {
          position: mapContainer.style.position,
          width: mapContainer.style.width,
          height: mapContainer.style.height,
          top: mapContainer.style.top,
          left: mapContainer.style.left,
          zIndex: mapContainer.style.zIndex,
          borderRadius: mapContainer.style.borderRadius,
          overflow: mapContainer.style.overflow
        };
        
        // Сохраняем оригинальные стили в data-атрибут
        mapContainer.setAttribute('data-original-styles', JSON.stringify(originalStyles));
        
        // Запоминаем текущую позицию скролла
        const scrollPosition = window.scrollY;
        document.body.setAttribute('data-scroll-position', scrollPosition);
        
        // Применяем стили для "псевдо-полноэкранного" режима
        mapContainer.style.position = 'fixed';
        mapContainer.style.width = '100vw';
        mapContainer.style.height = '100vh';
        mapContainer.style.top = '0';
        mapContainer.style.left = '0';
        mapContainer.style.right = '0';
        mapContainer.style.bottom = '0';
        mapContainer.style.zIndex = '9999';
        mapContainer.style.borderRadius = '0';
        mapContainer.style.overflow = 'hidden';
        
        // Специальные стили для iOS
        if (isIOS) {
          mapContainer.style.height = '100vh';
          mapContainer.style.maxHeight = '100vh';
          mapContainer.style.minHeight = '100vh';
          // Учитываем безопасные зоны iOS, но убираем боковые отступы для Android
          mapContainer.style.paddingTop = 'env(safe-area-inset-top, 0px)';
          mapContainer.style.paddingBottom = 'env(safe-area-inset-bottom, 0px)';
          // Убираем боковые отступы для предотвращения смещения карты
          mapContainer.style.paddingLeft = '0px';
          mapContainer.style.paddingRight = '0px';
        } else {
          // Для Android и других мобильных устройств убираем все отступы
          mapContainer.style.paddingTop = '0px';
          mapContainer.style.paddingBottom = '0px';
          mapContainer.style.paddingLeft = '0px';
          mapContainer.style.paddingRight = '0px';
          mapContainer.style.margin = '0px';
        }
        
        // Устанавливаем флаг полноэкранного режима
        setIsFullscreen(true);
        
        // Добавляем класс к контейнеру карты для мобильного полноэкранного режима
        mapContainer.classList.add('fullscreen-mobile');
        
        // Добавляем класс для body и html, чтобы запретить прокрутку
        document.body.classList.add('map-fullscreen-ios');
        document.documentElement.classList.add('map-fullscreen-ios');
        
        // Блокируем скролл body
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollPosition}px`;
        document.body.style.width = '100%';
        document.body.style.height = '100%';
        document.body.style.overflow = 'hidden';
        
        // Дополнительная блокировка для iOS
        if (isIOS) {
          document.body.style.webkitOverflowScrolling = 'touch';
          document.body.style.overscrollBehavior = 'none';
          document.documentElement.style.overflow = 'hidden';
          document.documentElement.style.position = 'fixed';
          document.documentElement.style.height = '100%';
          document.documentElement.style.width = '100%';
        }
        
        // Переключаемся на полноэкранный режим системы iOS при возможности
        if (document.documentElement.webkitRequestFullscreen) {
          try {
            // Пытаемся использовать нативный полноэкранный режим iOS
            document.documentElement.webkitRequestFullscreen();
          } catch (e) {
            // Нативный полноэкранный режим недоступен на iOS, используем альтернативу
          }
        }
      } else {
        // Восстанавливаем оригинальные стили при выходе из режима
        const mapContainer = mapContainerRef.current;
        const originalStyles = JSON.parse(mapContainer.getAttribute('data-original-styles') || '{}');
        
        // Применяем сохраненные стили
        Object.entries(originalStyles).forEach(([prop, value]) => {
          mapContainer.style[prop] = value;
        });
        
        // Восстанавливаем позицию скролла
        const scrollPosition = parseInt(document.body.getAttribute('data-scroll-position') || '0');
        
        // Снимаем флаг полноэкранного режима
        setIsFullscreen(false);
        
        // Убираем класс с контейнера карты
        mapContainer.classList.remove('fullscreen-mobile');
        
        // Удаляем класс с body и html
        document.body.classList.remove('map-fullscreen-ios');
        document.documentElement.classList.remove('map-fullscreen-ios');
        
        // Разблокируем скролл body
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.height = '';
        document.body.style.overflow = '';
        
        // Восстанавливаем стили для iOS
        if (isIOS) {
          document.body.style.webkitOverflowScrolling = '';
          document.body.style.overscrollBehavior = '';
          document.documentElement.style.overflow = '';
          document.documentElement.style.position = '';
          document.documentElement.style.height = '';
          document.documentElement.style.width = '';
        }
        
        window.scrollTo(0, scrollPosition);
        
        // Выходим из полноэкранного режима, если он был активирован
        if (document.webkitExitFullscreen) {
          try {
            document.webkitExitFullscreen();
          } catch (e) {
            // Ошибка при выходе из полноэкранного режима iOS
          }
        }
      }
    } else {
      // Стандартный подход для других устройств (не iOS)
      if (!isFullscreen) {
        if (mapContainerRef.current.requestFullscreen) {
          mapContainerRef.current.requestFullscreen();
        } else if (mapContainerRef.current.webkitRequestFullscreen) {
          mapContainerRef.current.webkitRequestFullscreen();
        } else if (mapContainerRef.current.msRequestFullscreen) {
          mapContainerRef.current.msRequestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      }
    }
  };
  const moveMapToLocation = (event) => {
    const selected = event.target.value;
    setSelectedLocation(selected); // Сохраняем выбранный район
    const newCoords = locations[selected];
    
    if (newCoords) {
        // Устанавливаем разные уровни зума для разных районов
        let zoomLevel = 11;
        
        // Настройка оптимального зума для каждого района
        if (selected === 'norilsk') zoomLevel = 11.5;      // Норильск - больше территория
        else if (selected === 'talnakh') zoomLevel = 11.8;  // Талнах - средний
        else if (selected === 'kaierkan') zoomLevel = 12;   // Кайеркан - компактный
        else if (selected === 'oganer') zoomLevel = 12.3;   // Оганер - самый компактный
        
        setViewport((prev) => ({
            ...prev,
            latitude: newCoords.latitude,
            longitude: newCoords.longitude,
            zoom: zoomLevel,
            transitionDuration: 1000 // Плавная анимация перемещения (1 секунда)
        }));
        
        // Прямое обновление текущего района при выборе в селекторе
        // Название района определяется автоматически на основе координат
        
        // Не устанавливаем флаг ручного выбора, 
        // плашка будет обновляться автоматически на основе координат
    }
};

  useEffect(() => {
    const handleFullscreenChange = () => {
      // Проверяем, используется ли мобильный или iOS подход для полноэкранного режима
      const isIOS = (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) || 
                    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) || 
                    /iPhone|iPad/.test(navigator.platform);
      const isMobile = (window.innerWidth <= 768) || 
                       /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // Для мобильных устройств не полагаемся на нативный fullscreen API,
      // так как используем псевдо-полноэкранный режим
      if (isIOS || isMobile) {
        // Не сбрасываем isFullscreen для мобильных устройств
        return;
      }
      
      // Для десктопных устройств используем стандартную проверку
      setIsFullscreen(
        document.fullscreenElement === mapContainerRef.current ||
          document.webkitFullscreenElement === mapContainerRef.current ||
          document.msFullscreenElement === mapContainerRef.current
      );
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    // Обработчик для выхода из полноэкранного режима при нажатии на Escape
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isFullscreen) {
        handleFullScreen();
      }
    };

    // Обработчик для iOS ориентации
    const handleOrientationChange = () => {
      if (isFullscreen) {
        // Улучшенная проверка для iOS устройств
        const isIOS = (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) || 
                     (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) || 
                     /iPhone|iPad/.test(navigator.platform);
        
        if ((isIOS || window.innerWidth <= 768) && mapContainerRef.current) {
          setTimeout(() => {
            // Используем viewport units для корректного размера
            mapContainerRef.current.style.height = '100vh';
            mapContainerRef.current.style.width = '100vw';
            mapContainerRef.current.style.maxHeight = '100vh';
            mapContainerRef.current.style.minHeight = '100vh';
            
            // Обновляем безопасные зоны для iOS, но убираем боковые отступы
            if (isIOS) {
              mapContainerRef.current.style.paddingTop = 'env(safe-area-inset-top, 0px)';
              mapContainerRef.current.style.paddingBottom = 'env(safe-area-inset-bottom, 0px)';
              // Убираем боковые отступы для предотвращения смещения карты
              mapContainerRef.current.style.paddingLeft = '0px';
              mapContainerRef.current.style.paddingRight = '0px';
            } else {
              // Для Android и других мобильных устройств убираем все отступы
              mapContainerRef.current.style.paddingTop = '0px';
              mapContainerRef.current.style.paddingBottom = '0px';
              mapContainerRef.current.style.paddingLeft = '0px';
              mapContainerRef.current.style.paddingRight = '0px';
              mapContainerRef.current.style.margin = '0px';
            }
          }, 300); // Увеличиваем задержку для iOS
        }
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("msfullscreenchange", handleFullscreenChange);
      document.removeEventListener('keydown', handleEscapeKey);
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreen]);
  
// Устанавливаем начальный зум при монтировании компонента
useEffect(() => {
  // При первой загрузке устанавливаем низкий зум для лучшей кластеризации
  if (houses && houses.length > 0) {
    setZoom(8);
    setViewport(prev => ({
      ...prev,
      zoom: 8
    }));
  }
}, [houses]);

// Обработчик изменения видимой области карты
const handleMoveEnd = useCallback((evt) => {
  setZoom(evt.viewState.zoom);
  
  try {
    // Получаем текущие границы видимой области
    const bounds = evt.target.getBounds().toArray();
    setViewBounds(bounds);
    
    // Больше не отслеживаем ручной выбор района - все определяется автоматически
  } catch (error) {
    // Ошибка при получении границ
  }
}, []);

// Обработчик загрузки карты
const handleMapLoad = useCallback((evt) => {
  try {
    // Получаем начальные границы видимой области
    const bounds = evt.target.getBounds().toArray();
    setViewBounds(bounds);
  } catch (error) {
    // Ошибка при получении начальных границ
  }
}, []);

// Функция определения района по координатам - оборачиваем в useCallback
const getDistrictFromCoordinates = useCallback((coordinates) => {
  if (!coordinates || coordinates.length !== 2) return null;
  
  const [longitude, latitude] = coordinates;
  
          // Проверка на слишком удаленную камеру
        // Если камера слишком далеко от всех районов, возвращаем "Не определен"
        // Создаем прямоугольник, охватывающий все районы с запасом
        const northBound = 69.59; // Немного севернее Талнаха
        const southBound = 69.32; // Немного южнее Норильска/Оганера
        const eastBound = 88.50;  // Немного восточнее Талнаха/Оганера
        const westBound = 87.65;  // Немного западнее Кайеркана
        
        // Если координаты далеко за пределами области с районами
        if (latitude > northBound + 0.2 || latitude < southBound - 0.2 ||
            longitude > eastBound + 0.2 || longitude < westBound - 0.2) {
          return null; // Возвращаем null, что означает "Не определен"
        }
        
        // Используем упрощенную логику определения районов на основе координат
        // Талнах (проверяем его первым, так как он дальше от других районов)
        if (latitude > 69.45) { // Талнах находится севернее остальных районов
          return "Талнах";
        }
        // Кайеркан (проверяем его вторым, так как он западнее)
        else if (longitude < 87.9) {
          return "Кайеркан";
        }
        // Оганер - определяется отдельно
        else if (Math.abs(longitude - locations.oganer.longitude) < 0.1 && 
                 Math.abs(latitude - locations.oganer.latitude) < 0.1) {
          return "Центральный";
        }
        // Центральный район (Норильск) - все остальное в пределах допустимой области
        else {
          return "Центральный";
        }
}, []);

// Определяем текущий район на основе центра карты
const currentDistrict = useMemo(() => {
  return getDistrictFromCoordinates([viewport.longitude, viewport.latitude]);
}, [viewport.longitude, viewport.latitude, getDistrictFromCoordinates]);

// Обновляем currentDistrictName при изменении currentDistrict
useEffect(() => {
  // Всегда обновляем название района на основе текущих координат
  if (currentDistrict) {
    setCurrentDistrictName(currentDistrict);
  } else {
    // Если район не определен (currentDistrict === null), 
    // устанавливаем null для корректного отображения "Не определен"
    setCurrentDistrictName(null);
  }
}, [currentDistrict]);

// Базовая кластеризация с учетом районов
const clusterIndex = useMemo(() => {
  if (!houses || houses.length === 0) return null;
  
  return new Supercluster({
    radius: 100, // Значительно увеличиваем радиус для более агрессивной кластеризации
    maxZoom: 17, // Повышаем максимальный зум для кластеризации
    minZoom: 0,  // Начальный зум для кластеризации
    minPoints: 3, // Минимум 3 точки для создания кластера
  }).load(
    houses.map((house) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: house.geometry.coordinates,
      },
      properties: { 
        cluster: false,
        ...house.properties,
        district: getDistrictFromCoordinates(house.geometry.coordinates) // Добавляем информацию о районе
      },
    }))
  );
}, [houses, getDistrictFromCoordinates]);

// Функция для изменения выбранных серий домов (для чекбоксов)
const handleSeriesChange = (event) => {
  const { value, checked } = event.target;
  let newSelectedSeries;

  // Если выбрана опция "Все серии"
  if (value === 'Все серии') {
    if (checked) {
      // Если выбраны "Все серии", снимаем выбор с других опций
      newSelectedSeries = ['Все серии'];
    } else if (selectedSeries.length === 1) {
      // Не даем снять выбор с опции "Все серии", если она единственная выбранная
      return;
    }
  } else {
    // Для других серий
    if (checked) {
      // Добавляем серию в выбранные и удаляем "Все серии"
      const filtered = selectedSeries.filter(item => item !== 'Все серии');
      newSelectedSeries = [...filtered, value];
    } else {
      // Удаляем серию из выбранных
      const filtered = selectedSeries.filter(item => item !== value);
      
      // Если ничего не выбрано, выбираем "Все серии"
      if (filtered.length === 0) {
        newSelectedSeries = ['Все серии'];
      } else {
        newSelectedSeries = filtered;
      }
    }
  }

  // Обновляем состояние
  setSelectedSeries(newSelectedSeries);

  // Вызываем переданную из родительского компонента функцию 
  // для обновления данных с сервера
  if (onSeriesChange) {
    // Передаем новый массив выбранных серий в родительский компонент
    onSeriesChange(newSelectedSeries);
  }
};

// Получаем кластеры с учетом видимой области, текущего района и выбранной серии
const clusters = useMemo(() => {
  if (!clusterIndex || !viewBounds) return houses || [];
  
  // Получаем прямоугольник видимой области с запасом
  const [[minLng, minLat], [maxLng, maxLat]] = viewBounds;
  const padding = 0.05; // Добавляем запас
  const bounds = [
    minLng - padding, 
    minLat - padding, 
    maxLng + padding, 
    maxLat + padding
  ];
  
  // Получаем все кластеры в видимой области
  const visibleClusters = clusterIndex.getClusters(bounds, Math.floor(zoom));
  
  // Фильтрация по серии домов
  const filteredClusters = selectedSeries.includes('Все серии') 
    ? visibleClusters 
    : visibleClusters.filter(cluster => {
        // Если это кластер, то он может содержать дома разных серий,
        // поэтому проверяем его содержимое
        if (cluster.properties.cluster) {
          // Получаем все точки в кластере
          const leaves = clusterIndex.getLeaves(
            cluster.properties.cluster_id, 
            Infinity
          );
          
          // Считаем точки выбранных серий
          const seriesPointCount = leaves.filter(leaf => 
            selectedSeries.includes(leaf.properties.series)
          ).length;
          
          // Сохраняем количество точек выбранных серий в свойствах кластера
          if (seriesPointCount > 0) {
            cluster.properties.series_point_count = seriesPointCount;
            return true;
          }
          return false;
        }
        // Для отдельных домов проверяем соответствие одной из выбранных серий
        return selectedSeries.includes(cluster.properties.series);
      });
  
  // Если зум высокий, декластеризуем только для текущего района
  if (zoom > 15) {
    return filteredClusters.map(cluster => {
      // Если это не кластер, проверяем его район
      if (!cluster.properties.cluster) {
        const clusterDistrict = cluster.properties.district;
        // Если точка не в текущем районе, заменяем её кластером
        if (clusterDistrict !== currentDistrict) {
          return null; // Не показываем дома из других районов при большом зуме
        }
        return cluster;
      }
      return cluster;
    }).filter(c => c !== null);
  }
  
  return filteredClusters;
}, [houses, zoom, clusterIndex, viewBounds, currentDistrict, selectedSeries]);

// Базовый размер кластера - увеличиваем размеры для большей наглядности
const getClusterSize = (pointCount) => {
  return pointCount < 20 ? 60 :
         pointCount < 50 ? 70 :
         pointCount < 100 ? 80 : 90;
};

// Обработчик движения карты
const handleMove = useCallback((evt) => {
  setViewport(evt.viewState);
}, []);

// Обработчик клика на кластер
const handleClusterClick = useCallback((clusterId, latitude, longitude) => {
  const expansionZoom = clusterIndex.getClusterExpansionZoom(clusterId);
  setViewport((prev) => ({
    ...prev,
    latitude,
    longitude,
    zoom: Math.min(expansionZoom + 1, 18),
    transitionDuration: 500
  }));
}, [clusterIndex]);



// Полностью переделаем функцию для блокировки прокрутки
const toggleBodyScroll = useCallback((shouldLock) => {
  if (window.innerWidth <= 768) {
    if (shouldLock) {
      // На мобильных устройствах инфо-панель будет позиционироваться поверх содержимого,
      // но не будем фиксировать body, чтобы избежать скачков при прокрутке
      document.body.classList.add('info-panel-open');
    } else {
      // Просто убираем класс, без манипуляций с прокруткой
      document.body.classList.remove('info-panel-open');
    }
  }
}, []);

// Обновим обработчик клика на маркер
const handleMarkerClick = useCallback((e, house) => {
  e.originalEvent.stopPropagation();
  setSelectedHouse(house);
  setSelectedHouseId(house.properties.id);
  setInfoPanelState('visible'); // Открываем панель при выборе дома
  toggleBodyScroll(true); // Блокируем прокрутку страницы
  if (onSelectBuilding) {
    onSelectBuilding(house.properties.id);
  }
}, [onSelectBuilding, toggleBodyScroll]);

  // Инициализируем район при загрузке компонента
  useEffect(() => {
    // Устанавливаем Центральный район по умолчанию
    setCurrentDistrictName("Центральный");
  }, []);

  // Состояния для модального окна редактирования
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [editingLabel, setEditingLabel] = useState("");
  const [editResult, setEditResult] = useState(null);
  // Новое состояние для отслеживания ошибок валидации
  const [validationError, setValidationError] = useState(null);
  
  // Проверка доступа к редактированию - только для ролей 3 и 4
  const canEditFields = userRole === 3 || userRole === 4;

  // Функция для определения типа поля и его валидации
  const getFieldType = (fieldName) => {
    // Булевы поля
    const booleanFields = [
      "is_emergency", "is_demolished", "accessibility_features", 
      "cultural_heritage_status", "garbage_chute"
      // Удалены следующие поля, так как они не булевы в разделе "Инженерные системы":
      // "ventilation", "drainage", "stormwater_drainage", "gas_supply", 
      // "hot_water_supply", "fire_extinguishing_system", "heat_supply", 
      // "cold_water_supply", "electricity_supply"
    ];
    
    // Поля износа (0-100%)
    const wearFields = [
      "wear_percent", "foundation_wear", "facade_wear", "roof_wear", 
      "internal_walls_wear", "heating_system_wear", "cold_water_system_wear", 
      "hot_water_system_wear", "sewer_system_wear", "heating_riser_wear",
      "hot_water_riser_wear", "cold_water_riser_wear", "hot_water_shutoff_wear", 
      "heating_shutoff_wear", "cold_water_shutoff_wear", "electricity_system_wear",
      "floor_wear", "heating_device_wear"
    ];
    
    // Числовые поля
    const numericFields = [
      "year_built", "num_floors", "num_living_units", "latitude", "longitude",
      "postal_code", "market_price_per_sqm", "year_commissioned", "num_apartments",
      "num_non_residential_units", "num_balconies", "num_entrances", "max_floors",
      "min_floors", "parking_area", "total_building_area", "living_area", 
      "non_living_area", "common_property_area", "land_plot_area", "foundation_area",
      "house_inputs_count", "electricity_inputs_count", "foundation_last_capital_repair_year",
      "facade_last_capital_repair_year", "electricity_last_capital_repair_year",
      "sewer_last_capital_repair_year", "roof_last_capital_repair_year"
    ];
    
    if (booleanFields.includes(fieldName)) {
      return "boolean";
    } else if (wearFields.includes(fieldName)) {
      return "wear";
    } else if (numericFields.includes(fieldName)) {
      return "numeric";
    } else {
      return "text";
    }
  };

  // Функция для отображения данных с учетом роли пользователя и возможности редактирования
  const renderFieldData = (value, fieldName, label, isBasicDataField = false) => {
    // Конвертируем булевые значения в текст "Да"/"Нет"
    let displayValue;
    
    // Особая обработка для поля cultural_heritage_status
    if (fieldName === "cultural_heritage_status" && (value === undefined || value === null)) {
      // По умолчанию, если не указано, то объект не является культурным наследием
      displayValue = "Нет";
    }
    // Проверяем явно все возможные булевые значения
    else if (value === true || value === 'true') {
      displayValue = "Да";
    } else if (value === false || value === 'false') {
      displayValue = "Нет";
    } else if (value !== undefined && value !== null) {
      // Если это не булево значение, но оно существует
      displayValue = value;
    } else {
      // Если значение не определено
      displayValue = isBasicDataField ? "Нет данных" : 
                    (userRole === 1 ? <span style={{ color: '#3DC795', fontWeight: 'bold' }}>Доступно с подпиской</span> : "Нет данных");
    }
    
    // Добавляем кнопку редактирования для пользователей с соответствующими правами
    return (
      <div className="building-info-row">
        <span className="info-value">{displayValue}</span>
        {canEditFields && (
          <button 
            className="edit-field-button" 
            onClick={() => openEditModal(fieldName, value, label)}
            title="Редактировать"
          >
            <span className="edit-icon">✎</span>
          </button>
        )}
      </div>
    );
  };

  // Функция открытия модального окна для редактирования
  const openEditModal = (fieldName, value, label) => {
    setEditingField(fieldName);
    setEditingValue(value !== undefined ? value.toString() : "");
    setEditingLabel(label);
    setEditResult(null);
    setValidationError(null);
    setIsEditModalOpen(true);
    
    // Для мобильных устройств - блокируем прокрутку body, чтобы не было проблем с прокруткой
    if (window.innerWidth <= 768) {
      document.body.classList.add('no-scroll');
      document.body.classList.add('modal-active'); // Добавляем класс для приоритизации модального окна
      
      // Добавляем небольшую задержку перед фокусировкой на поле ввода на мобильных устройствах
      setTimeout(() => {
        const inputField = document.getElementById('field-value');
        if (inputField) {
          inputField.focus();
        }
      }, 300);
    }
  };

  // Функция закрытия модального окна
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingField(null);
    setEditingValue("");
    setEditingLabel("");
    setEditResult(null);
    
    // Разблокируем прокрутку body при закрытии модального окна
    if (window.innerWidth <= 768) {
      document.body.classList.remove('no-scroll');
      document.body.classList.remove('modal-active'); // Удаляем класс при закрытии
    }
  };

  // Функция для сохранения отредактированного значения
  const saveEditedField = async () => {
    // Если нет выбранного дома или редактируемого поля, выходим
    if (!selectedHouse || !editingField) return;
    
    // Валидация значения перед отправкой
    const validation = validateFieldValue(editingField, editingValue);
    if (!validation.isValid) {
      setValidationError(validation.message);
      return;
    }
    
    setValidationError(null);
    setEditResult({ type: 'loading', message: 'Сохраняем изменения...' });
    
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setEditResult({ type: 'error', message: 'Необходима авторизация' });
        return;
      }
      
      // Преобразуем значение в зависимости от типа поля
      let valueToSend = editingValue;
      const fieldType = getFieldType(editingField);
      
      if (fieldType === "boolean") {
        valueToSend = editingValue === "true";
      } else if (fieldType === "numeric" || fieldType === "wear") {
        valueToSend = parseFloat(editingValue);
      }
      
      // Данные для обновления - создаем объект, где ключ - имя поля
      const updateData = {
        data: {
          [editingField]: valueToSend
        }
      };
      
      const response = await fetch(`${API_URL}/House/update-houses/${selectedHouse.properties.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include', // Добавляем передачу куки вместе с запросом
        body: JSON.stringify(updateData)
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          setEditResult({ type: 'error', message: 'Ошибка авторизации. Пожалуйста, войдите в систему повторно.' });
          return;
        }
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Ошибка при обновлении данных');
      }
      
      const result = await response.json();
      
      // Обновляем данные на клиенте, если API вернул обновленные данные
      if (result.house) {
        // Обновление данных в локальном состоянии
        if (onSelectBuilding) {
          onSelectBuilding(selectedHouse.properties.id);
        }
      }
      
      setEditResult({ type: 'success', message: 'Данные успешно обновлены' });
      
      // Закрываем модальное окно через 1.5 секунды после успешного сохранения
      setTimeout(() => {
        closeEditModal();
      }, 1500);
      
    } catch (error) {
      setEditResult({ type: 'error', message: `Ошибка: ${error.message}` });
    }
  };

  // Новая функция для переключения состояния панели фильтров
  const toggleControls = () => {
    setIsControlsExpanded(!isControlsExpanded);
  };

  // Обновляем позицию выпадающего списка при открытии
  useEffect(() => {
    if (showSeriesList && seriesContainerRef.current) {
      const rect = seriesContainerRef.current.getBoundingClientRect();
      const mapContainer = document.querySelector('.map-container');
      const mapRect = mapContainer ? mapContainer.getBoundingClientRect() : { left: 0, top: 0 };
      
      // Проверяем размер экрана для адаптивного позиционирования
      if (window.innerWidth <= 576) {
        // Для мобильных - фиксированное положение сверху
        setDropdownPosition({
          top: 120,
          left: 10
        });
      } else {
        // Для десктопов - позиционирование относительно контейнера
        setDropdownPosition({
          top: rect.bottom - mapRect.top + 5,
          left: rect.left - mapRect.left
        });
      }
    }
  }, [showSeriesList]);

  // Обновим функцию для закрытия инфо-панели
  const closeInfoPanel = () => {
    if (window.innerWidth <= 768) {
      // На мобильных устройствах сначала скрываем панель с анимацией
      setInfoPanelState('hidden');
      
      // Не блокируем прокрутку при показе панели
      toggleBodyScroll(false);
      
      // Через время анимации убираем выбранный дом
      setTimeout(() => {
        setSelectedHouse(null);
        setSelectedHouseId(null);
      }, 300); // Время должно соответствовать длительности CSS анимации
    } else {
      // На десктопе закрываем сразу
      setSelectedHouse(null);
      setSelectedHouseId(null);
    }
  };

  // Обработчик клика на полупрозрачный фон (overlay)
  const handleOverlayClick = () => {
    closeInfoPanel();
  };



  // Определяем состояние темы 
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Проверяем текущую тему при загрузке компонента
  useEffect(() => {
    const checkDarkMode = () => {
      const bodyDarkMode = document.body.classList.contains('dark-mode');
      setIsDarkMode(bodyDarkMode);
    };
    
    // Проверяем при монтировании
    checkDarkMode();
    
    // Создаем MutationObserver для отслеживания изменений в классах body
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    // Начинаем наблюдение
    observer.observe(document.body, { attributes: true });
    
    // Очищаем наблюдателя при размонтировании
    return () => observer.disconnect();
  }, []);

  // При монтировании/размонтировании компонента обеспечим корректное состояние скролла
  useEffect(() => {
    return () => {
      // При размонтировании компонента гарантированно восстанавливаем прокрутку
      if (document.body.classList.contains('no-scroll')) {
        toggleBodyScroll(false);
      }
    };
  }, [toggleBodyScroll]);
  
  // Блокируем/разблокируем прокрутку при изменении состояния панели
  useEffect(() => {
    if (selectedHouse && window.innerWidth <= 768) {
      if (infoPanelState === 'visible') {
        toggleBodyScroll(true);
      } else if (infoPanelState === 'hidden') {
        toggleBodyScroll(false);
      }
    }
  }, [infoPanelState, selectedHouse, toggleBodyScroll]);

  // Функция валидации значений в зависимости от типа поля
  const validateFieldValue = (fieldName, value) => {
    const fieldType = getFieldType(fieldName);
    
    if (fieldType === "boolean") {
      // Булевы значения уже валидны из-за радиокнопок
      return { isValid: true, message: "" };
    } else if (fieldType === "wear") {
      // Валидация полей износа (0-100%)
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return { isValid: false, message: "Введите число от 0 до 100" };
      } else if (numValue < 0 || numValue > 100) {
        return { isValid: false, message: "Значение должно быть от 0 до 100" };
      }
      return { isValid: true, message: "" };
    } else if (fieldType === "numeric") {
      // Валидация числовых полей
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return { isValid: false, message: "Введите числовое значение" };
      }
      // Дополнительная валидация для специфических полей
      if (fieldName.includes("year") && (numValue < 1900 || numValue > new Date().getFullYear())) {
        return { isValid: false, message: `Год должен быть между 1900 и ${new Date().getFullYear()}` };
      }
      return { isValid: true, message: "" };
    }
    
    // Для текстовых полей просто проверяем, что они не пустые
    if (value.trim() === "") {
      return { isValid: false, message: "Поле не может быть пустым" };
    }
    
    return { isValid: true, message: "" };
  };

  // Рендеринг компонента
  return (
    <section className="map-section" id="map">
      <h2 className="map-title section-title-underlined">Карта</h2>
      <p className="map-description">
        Интерактивная 3D-карта состояния жилого фонда Норильска.
      </p>
      
      {/* Добавляем поиск по адресам */}
      <div className={`address-search ${isDarkMode ? 'search-dark' : 'search-light'}`}>
        <input 
          type="text" 
          placeholder="Поиск по адресу..." 
          onChange={(e) => {
            // Создаем новый элемент datalist и добавляем его в DOM
            const list = document.getElementById('addresses');
            if (list) {
              list.innerHTML = '';
              
              // Если строка поиска не пустая
              if (e.target.value.trim().length > 0) {
                // Фильтруем дома по совпадению адреса
                const filteredHouses = houses.filter(house => 
                  house.properties.address.toLowerCase().includes(e.target.value.toLowerCase())
                ).slice(0, 10); // Берем только первые 10 результатов
                
                // Добавляем варианты
                filteredHouses.forEach(house => {
                  const option = document.createElement('option');
                  option.value = house.properties.address;
                  option.setAttribute('data-id', house.properties.id);
                  list.appendChild(option);
                });
              }
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const value = e.target.value;
              const list = document.getElementById('addresses');
              const options = list.getElementsByTagName('option');
              for (let i = 0; i < options.length; i++) {
                if (options[i].value === value) {
                  const id = parseInt(options[i].getAttribute('data-id'));
                  const house = houses.find(h => h.properties.id === id);
                  if (house) {
                    // Выбираем дом и перемещаем к нему карту
                    setSelectedHouse(house);
                    setSelectedHouseId(id);
                    
                    if (onSelectBuilding) {
                      onSelectBuilding(id);
                    }
                    
                    // Перемещаем карту к выбранному дому
                    const [longitude, latitude] = house.geometry.coordinates;
                    setViewport(prev => ({
                      ...prev,
                      longitude,
                      latitude,
                      zoom: 16, // Увеличиваем масштаб
                      transitionDuration: 1000 // Плавная анимация
                    }));
                    
                    // Устанавливаем состояние информационной панели на 'visible'
                    setInfoPanelState('visible');
                    
                    break;
                  }
                }
              }
            }
          }}
          list="addresses"
        />
        <button 
          className="search-button"
          onClick={() => {
            const input = document.querySelector('.address-search input');
            const value = input.value;
            if (value.trim().length > 0) {
              const list = document.getElementById('addresses');
              const options = list.getElementsByTagName('option');
              for (let i = 0; i < options.length; i++) {
                if (options[i].value === value) {
                  const id = parseInt(options[i].getAttribute('data-id'));
                  const house = houses.find(h => h.properties.id === id);
                  if (house) {
                    // Выбираем дом и перемещаем к нему карту
                    setSelectedHouse(house);
                    setSelectedHouseId(id);
                    
                    if (onSelectBuilding) {
                      onSelectBuilding(id);
                    }
                    
                    // Перемещаем карту к выбранному дому
                    const [longitude, latitude] = house.geometry.coordinates;
                    setViewport(prev => ({
                      ...prev,
                      longitude,
                      latitude,
                      zoom: 16, // Увеличиваем масштаб
                      transitionDuration: 1000 // Плавная анимация
                    }));
                    
                    // Устанавливаем состояние информационной панели на 'visible'
                    setInfoPanelState('visible');
                    
                    break;
                  }
                }
              }
            }
          }}
          aria-label="Поиск"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
          </svg>
        </button>
        <datalist id="addresses"></datalist>
      </div>
      
      <div className="map-container" ref={mapContainerRef}>
        <Map
          {...viewport}
          onMoveEnd={handleMoveEnd}
          onLoad={handleMapLoad}
          style={{ width: "100%", height: "100%" }}
          mapStyle="mapbox://styles/igorsergeevichisit/cm7oy4ihz005801sd8zmfayke"
          mapboxAccessToken={MAPBOX_TOKEN}
          onMove={handleMove}
        >
          {/* Селекторы в одном контейнере с возможностью сворачивания */}
          <div className={`controls-container`}>
            {/* Заголовок панели с кнопкой сворачивания */}
            <div className="controls-header" onClick={toggleControls}>
              <div className="controls-title">Фильтры карты</div>
              <div className={`controls-toggle-icon ${!isControlsExpanded ? 'collapsed' : ''}`}>▼</div>
            </div>
            
            {/* Содержимое панели фильтров */}
            <div className={`controls-content ${isControlsExpanded ? 'expanded' : 'collapsed'}`}>
              {/* Селектор районов */}
              <div className="control-block">
                <label htmlFor="locations">Выберите район:</label>
                <select id="locations" value={selectedLocation} onChange={moveMapToLocation}>
                  <option value="norilsk">Норильск</option>
                  <option value="talnakh">Талнах</option>
                  <option value="kaierkan">Кайеркан</option>
                  <option value="oganer">Оганер</option>
                </select>
              </div>

              {/* Селектор серий домов с чекбоксами */}
              <div className="control-block">
                <div className="selector-header">
                  <label>Серии домов:</label>
                  {!isLoadingSeries && !seriesError && (
                    <button 
                      className="refresh-button" 
                      onClick={fetchBuildingSeries} 
                      title="Обновить список серий"
                    >
                      ↻
                    </button>
                  )}
                </div>
                
                <div className="series-selector-container" ref={seriesContainerRef}>
                  <div 
                    className="selected-series" 
                    onClick={() => showSeriesList ? closeSeriesList() : setShowSeriesList(true)}
                  >
                    {selectedSeries.includes('Все серии') 
                      ? 'Все серии' 
                      : `Выбрано: ${selectedSeries.length}`}
                    <span className={`dropdown-arrow ${showSeriesList ? 'open' : ''}`}>▼</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Индикатор текущего района отдельно */}
          <div className={`current-district-indicator`}>
            Район: {currentDistrictName || "Не определен"}
          </div>

          {/* Выносим выпадающий список вне свернутого блока */}
          {showSeriesList && (
            <>
              {/* Полупрозрачная подложка для мобильных устройств */}
              <div 
                className={`series-list-overlay ${seriesListClosing ? 'closing' : ''}`}
                onClick={closeSeriesList}
              />
              
              <div 
                className={`series-checkbox-list ${seriesListClosing ? 'closing' : ''}`}
                ref={seriesListRef}
                style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
              >
                {/* Кнопка закрытия для мобильных устройств */}
                <div className="close-checkbox-list">
                  <span onClick={closeSeriesList}>✖</span>
                </div>
                
                {isLoadingSeries ? (
                  <div className="loading-message">Загрузка серий...</div>
                ) : seriesError ? (
                  <div className="error-message">
                    Ошибка загрузки серий. <button onClick={fetchBuildingSeries}>Повторить</button>
                  </div>
                ) : (
                  <div className="checkbox-group">
                    <label className="checkbox-item">
                      <input 
                        type="checkbox" 
                        value="Все серии" 
                        checked={selectedSeries.includes('Все серии')}
                        onChange={handleSeriesChange}
                      />
                      <span>Все серии</span>
                    </label>
                    
                    {buildingSeries.filter(series => series !== 'Все серии').map((series) => (
                      <label key={series} className="checkbox-item">
                        <input 
                          type="checkbox" 
                          value={series}
                          checked={selectedSeries.includes(series)}
                          onChange={handleSeriesChange}
                        />
                        <span>{series}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Маркеры с фильтрацией */}
          {clusters.map((cluster) => {
            const [longitude, latitude] = cluster.geometry.coordinates;

            // Если это кластер
            if (cluster.properties.cluster) {
              // Используем series_point_count если выбраны конкретные серии, иначе общее количество
              const pointCount = !selectedSeries.includes('Все серии') && cluster.properties.series_point_count
                ? cluster.properties.series_point_count
                : cluster.properties.point_count;
                
              const size = getClusterSize(pointCount);
                
              return (
                <Marker key={cluster.id} longitude={longitude} latitude={latitude}>
                  <div
                    className="cluster-marker"
                    style={{
                      width: `${size}px`,
                      height: `${size}px`,
                      fontSize: pointCount > 99 ? '20px' : pointCount > 50 ? '18px' : '16px',
                      boxShadow: '0 0 15px rgba(0,0,0,0.5)' // Добавляем более заметную тень
                    }}
                    onClick={() => handleClusterClick(
                      cluster.properties.cluster_id, 
                      latitude, 
                      longitude
                    )}
                  >
                    {pointCount} {/* Отображаем количество домов в кластере */}
                  </div>
                </Marker>
              );
            }

            // Если это одиночная метка
            return (
              <Marker
                key={cluster.properties.id}
                longitude={longitude}
                latitude={latitude}
                onClick={(e) => handleMarkerClick(e, cluster)}
              >
                <img src={getMarkerIcon(cluster, selectedHouseId)} alt="Дом" className="custom-marker" />
              </Marker>
            );
          })}
            
          {/* Легенда карты */}
          <div id="legend" className={`legend ${legendExpanded ? "expanded" : ""}`}>
            <div id="legend-content" style={{ maxHeight: legendExpanded ? "300px" : "0px" }}>
                <div className="legend-item">
                    <span className="legend-color" style={{ backgroundColor: "#66cc33" }}></span>
                    <span>Стандартные дома</span>
                </div>
                <div className="legend-item">
                    <span className="legend-color" style={{ backgroundColor: "#ff931e" }}></span>
                    <span>Сталинки</span>
                </div>
                <div className="legend-item">
                    <span className="legend-color" style={{ backgroundColor: "#fc3" }}></span>
                    <span>Износ больше 50%</span>
                </div>
                <div className="legend-item">
                    <span className="legend-color" style={{ backgroundColor: "#f33" }}></span>
                    <span>Аварийные дома</span>
                </div>
                <div className="legend-item">
                    <span className="legend-color" style={{ backgroundColor: "#000" }}></span>
                    <span>Снесённые дома</span>
                </div>
            </div>

            {/* Кнопка разворачивания */}
            <div id="legend-toggle" onClick={toggleLegend}>
                <span>Легенда</span>
                <img 
                    src={require("../assets/img/expand arrow.png")}
                    alt="Развернуть" 
                    id="toggle-legend-icon"
                    className={legendExpanded ? "" : "collapsed"}
                />
            </div>
          </div>
        </Map>

        {/* Кнопка разворота карты - Меняем положение в зависимости от состояния */}
        <button 
          className={`fullscreen-button ${isFullscreen ? "fullscreen-button-expanded" : ""} ${selectedHouse ? "fullscreen-button-with-panel" : ""}`} 
          onClick={handleFullScreen}
        >
          <img
            src={isFullscreen ? IconSver : IconRazv}
            alt={isFullscreen ? "Свернуть карту" : "Развернуть карту"}
          />
        </button>

        {/* Полупрозрачный фон за инфо-панелью */}
        {selectedHouse && window.innerWidth <= 768 && (
          <div 
            className={`info-panel-overlay ${infoPanelState === 'hidden' ? 'hidden' : ''}`} 
            onClick={handleOverlayClick}
          />
        )}
        
        {/* Панель информации */}
        {selectedHouse && (
          <div 
            id="info-panel" 
            className={`info-panel ${infoPanelState} ${isDarkMode ? 'info-panel-dark' : 'info-panel-light'}`}
            ref={infoPanelRef}
          >
            <div 
              id="info-header" 
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <h3>{selectedHouse.properties.address}</h3>
              <span id="close-info-btn" onClick={closeInfoPanel}>✖</span>
            </div>
            <div id="info-body">
              <details open>
                <summary>Основные данные</summary>
                  <div className="info-item"><b>Год постройки:</b> {renderFieldData(selectedBuildingDetails?.properties?.year_built || selectedHouse.properties.year_built, "year_built", "Год постройки", true)}</div>
                  <div className="info-item"><b>Количество этажей:</b> {renderFieldData(selectedBuildingDetails?.properties?.num_floors || selectedHouse.properties.num_floors, "num_floors", "Количество этажей", true)}</div>
                  <div className="info-item"><b>Жилых помещений:</b> {renderFieldData(selectedBuildingDetails?.properties?.num_living_units || selectedHouse.properties.num_living_units, "num_living_units", "Жилых помещений", true)}</div>
                  <div className="info-item"><b>Серия:</b> {renderFieldData(selectedBuildingDetails?.properties?.series || selectedHouse.properties.series, "series", "Серия", true)}</div>
                  <div className="info-item"><b>Дом признан аварийным:</b> {renderFieldData(selectedBuildingDetails?.properties?.is_emergency || selectedHouse.properties.is_emergency, "is_emergency", "Дом признан аварийным", true)}</div>
                  <div className="info-item"><b>Кадастровый номер:</b> {renderFieldData(selectedBuildingDetails?.properties?.cadastral_number || selectedHouse.properties.cadastral_number, "cadastral_number", "Кадастровый номер")}</div>
                  <div className="info-item"><b>Код ОКТМО:</b> {renderFieldData(selectedBuildingDetails?.properties?.oktmo_code || selectedHouse.properties.oktmo_code, "oktmo_code", "Код ОКТМО")}</div>
                  <div className="info-item"><b>Управляющая компания:</b> {renderFieldData(selectedBuildingDetails?.properties?.management_company || selectedHouse.properties.management_company, "management_company", "Управляющая компания", true)}</div>
              </details>

              <details>
                  <summary>Внутренние стены</summary>
                  <div className="info-item"><b>Тип внутренних стен:</b> {renderFieldData(selectedBuildingDetails?.properties?.internal_walls_type || selectedHouse.properties.internal_walls_type, "internal_walls_type", "Тип внутренних стен")}</div>
                  <div className="info-item"><b>Физический износ внутренних стен (%):</b> {renderFieldData(selectedBuildingDetails?.properties?.internal_walls_wear || selectedHouse.properties.internal_walls_wear, "internal_walls_wear", "Физический износ внутренних стен (%)")}</div>
              </details>

              <details>
                  <summary>Запорная арматура</summary>
                  <div className="info-item"><b>Физический износ арматуры системы ГВС (%):</b> {renderFieldData(selectedBuildingDetails?.properties?.hot_water_shutoff_wear || selectedHouse.properties.hot_water_shutoff_wear, "hot_water_shutoff_wear", "Физический износ арматуры системы ГВС (%)")}</div>
                  <div className="info-item"><b>Физический износ арматуры системы отопления (%):</b> {renderFieldData(selectedBuildingDetails?.properties?.heating_shutoff_wear || selectedHouse.properties.heating_shutoff_wear, "heating_shutoff_wear", "Физический износ арматуры системы отопления (%)")}</div>
                  <div className="info-item"><b>Физический износ арматуры системы ХВС (%):</b> {renderFieldData(selectedBuildingDetails?.properties?.cold_water_shutoff_wear || selectedHouse.properties.cold_water_shutoff_wear, "cold_water_shutoff_wear", "Физический износ арматуры системы ХВС (%)")}</div>
              </details>

              <details>
                  <summary>Инженерные системы</summary>
                  <div className="info-item"><b>Вентиляция:</b> {renderFieldData(selectedBuildingDetails?.properties?.ventilation || selectedHouse.properties.ventilation, "ventilation", "Вентиляция")}</div>
                  <div className="info-item"><b>Дренаж:</b> {renderFieldData(selectedBuildingDetails?.properties?.drainage || selectedHouse.properties.drainage, "drainage", "Дренаж")}</div>
                  <div className="info-item"><b>Ливневая канализация:</b> {renderFieldData(selectedBuildingDetails?.properties?.stormwater_drainage || selectedHouse.properties.stormwater_drainage, "stormwater_drainage", "Ливневая канализация")}</div>
                  <div className="info-item"><b>Газоснабжение:</b> {renderFieldData(selectedBuildingDetails?.properties?.gas_supply || selectedHouse.properties.gas_supply, "gas_supply", "Газоснабжение")}</div>
                  <div className="info-item"><b>Горячее водоснабжение:</b> {renderFieldData(selectedBuildingDetails?.properties?.hot_water_supply || selectedHouse.properties.hot_water_supply, "hot_water_supply", "Горячее водоснабжение")}</div>
                  <div className="info-item"><b>Система пожаротушения:</b> {renderFieldData(selectedBuildingDetails?.properties?.fire_extinguishing_system || selectedHouse.properties.fire_extinguishing_system, "fire_extinguishing_system", "Система пожаротушения")}</div>
                  <div className="info-item"><b>Теплоснабжение:</b> {renderFieldData(selectedBuildingDetails?.properties?.heat_supply || selectedHouse.properties.heat_supply, "heat_supply", "Теплоснабжение")}</div>
                  <div className="info-item"><b>Холодное водоснабжение:</b> {renderFieldData(selectedBuildingDetails?.properties?.cold_water_supply || selectedHouse.properties.cold_water_supply, "cold_water_supply", "Холодное водоснабжение")}</div>
                  <div className="info-item"><b>Электроснабжение:</b> {renderFieldData(selectedBuildingDetails?.properties?.electricity_supply || selectedHouse.properties.electricity_supply, "electricity_supply", "Электроснабжение")}</div>
              </details>

              <details>
                  <summary>Крыша</summary>
                  <div className="info-item"><b>Форма крыши:</b> {renderFieldData(selectedBuildingDetails?.properties?.roof_shape || selectedHouse.properties.roof_shape, "roof_shape", "Форма крыши")}</div>
                  <div className="info-item"><b>Несущая конструкция крыши:</b> {renderFieldData(selectedBuildingDetails?.properties?.roof_supporting_structure || selectedHouse.properties.roof_supporting_structure, "roof_supporting_structure", "Несущая конструкция крыши")}</div>
                  <div className="info-item"><b>Тип кровли:</b> {renderFieldData(selectedBuildingDetails?.properties?.roof_type || selectedHouse.properties.roof_type, "roof_type", "Тип кровли")}</div>
                  <div className="info-item"><b>Материал утепления крыши:</b> {renderFieldData(selectedBuildingDetails?.properties?.roof_insulation_layers || selectedHouse.properties.roof_insulation_layers, "roof_insulation_layers", "Материал утепления крыши")}</div>
                  <div className="info-item"><b>Физический износ крыши (%):</b> {renderFieldData(selectedBuildingDetails?.properties?.roof_wear || selectedHouse.properties.roof_wear, "roof_wear", "Физический износ крыши (%)")}</div>
                  <div className="info-item"><b>Год последнего капитального ремонта крыши:</b> {renderFieldData(selectedBuildingDetails?.properties?.roof_last_capital_repair_year || selectedHouse.properties.roof_last_capital_repair_year, "roof_last_capital_repair_year", "Год последнего капитального ремонта крыши")}</div>
              </details>

              <details>
                  <summary>Отопительные приборы</summary>
                  <div className="info-item"><b>Тип отопительных приборов:</b> {renderFieldData(selectedBuildingDetails?.properties?.heating_device_type || selectedHouse.properties.heating_device_type, "heating_device_type", "Тип отопительных приборов")}</div>
                  <div className="info-item"><b>Физический износ отопительных приборов (%):</b> {renderFieldData(selectedBuildingDetails?.properties?.heating_device_wear || selectedHouse.properties.heating_device_wear, "heating_device_wear", "Физический износ отопительных приборов (%)")}</div>
              </details>

              <details>
                  <summary>Перекрытия</summary>
                  <div className="info-item"><b>Тип перекрытий:</b> {renderFieldData(selectedBuildingDetails?.properties?.floor_type || selectedHouse.properties.floor_type, "floor_type", "Тип перекрытий")}</div>
                  <div className="info-item"><b>Физический износ перекрытий (%):</b> {renderFieldData(selectedBuildingDetails?.properties?.floor_wear || selectedHouse.properties.floor_wear, "floor_wear", "Физический износ перекрытий (%)")}</div>
              </details>

              <details>
                  <summary>Система водоотведения</summary>
                  <div className="info-item"><b>Тип системы водоотведения:</b> {renderFieldData(selectedBuildingDetails?.properties?.sewer_system_type || selectedHouse.properties.sewer_system_type, "sewer_system_type", "Тип системы водоотведения")}</div>
                  <div className="info-item"><b>Материал сети водоотведения:</b> {renderFieldData(selectedBuildingDetails?.properties?.sewer_network_material || selectedHouse.properties.sewer_network_material, "sewer_network_material", "Материал сети водоотведения")}</div>
                  <div className="info-item"><b>Физический износ системы водоотведения (%):</b> {renderFieldData(selectedBuildingDetails?.properties?.sewer_system_wear || selectedHouse.properties.sewer_system_wear, "sewer_system_wear", "Физический износ системы водоотведения (%)")}</div>
                  <div className="info-item"><b>Год последнего капитального ремонта системы водоотведения:</b> {renderFieldData(selectedBuildingDetails?.properties?.sewer_last_capital_repair_year || selectedHouse.properties.sewer_last_capital_repair_year, "sewer_last_capital_repair_year", "Год последнего капитального ремонта системы водоотведения")}</div>
              </details>

              <details>
                  <summary>Система горячего водоснабжения</summary>
                  <div className="info-item"><b>Тип системы горячего водоснабжения:</b> {renderFieldData(selectedBuildingDetails?.properties?.hot_water_system_type || selectedHouse.properties.hot_water_system_type, "hot_water_system_type", "Тип системы горячего водоснабжения")}</div>
                  <div className="info-item"><b>Материал системы горячего водоснабжения:</b> {renderFieldData(selectedBuildingDetails?.properties?.hot_water_system_material || selectedHouse.properties.hot_water_system_material, "hot_water_system_material", "Материал системы горячего водоснабжения")}</div>
                  <div className="info-item"><b>Материал теплоизоляции системы ГВС:</b> {renderFieldData(selectedBuildingDetails?.properties?.hot_water_system_insulation_material || selectedHouse.properties.hot_water_system_insulation_material, "hot_water_system_insulation_material", "Материал теплоизоляции системы ГВС")}</div>
                  <div className="info-item"><b>Материал стояков ГВС:</b> {renderFieldData(selectedBuildingDetails?.properties?.hot_water_risers_material || selectedHouse.properties.hot_water_risers_material, "hot_water_risers_material", "Материал стояков ГВС")}</div>
                  <div className="info-item"><b>Физический износ системы ГВС (%):</b> {renderFieldData(selectedBuildingDetails?.properties?.hot_water_system_wear || selectedHouse.properties.hot_water_system_wear, "hot_water_system_wear", "Физический износ системы ГВС (%)")}</div>
              </details>

              <details>
                  <summary>Система отопления</summary>
                  <div className="info-item"><b>Тип распределения системы отопления:</b> {renderFieldData(selectedBuildingDetails?.properties?.heating_distribution_type || selectedHouse.properties.heating_distribution_type, "heating_distribution_type", "Тип распределения системы отопления")}</div>
                  <div className="info-item"><b>Материал системы отопления:</b> {renderFieldData(selectedBuildingDetails?.properties?.heating_system_material || selectedHouse.properties.heating_system_material, "heating_system_material", "Материал системы отопления")}</div>
                  <div className="info-item"><b>Материал теплоизоляции системы отопления:</b> {renderFieldData(selectedBuildingDetails?.properties?.heating_system_insulation_material || selectedHouse.properties.heating_system_insulation_material, "heating_system_insulation_material", "Материал теплоизоляции системы отопления")}</div>
                  <div className="info-item"><b>Физический износ системы отопления (%):</b> {renderFieldData(selectedBuildingDetails?.properties?.heating_system_wear || selectedHouse.properties.heating_system_wear, "heating_system_wear", "Физический износ системы отопления (%)")}</div>
                  <div className="info-item"><b>Физический износ стояков отопления (%):</b> {renderFieldData(selectedBuildingDetails?.properties?.heating_riser_wear || selectedHouse.properties.heating_riser_wear, "heating_riser_wear", "Физический износ стояков отопления (%)")}</div>
              </details>

              <details>
                  <summary>Система холодного водоснабжения</summary>
                  <div className="info-item"><b>Тип системы холодного водоснабжения:</b> {renderFieldData(selectedBuildingDetails?.properties?.cold_water_supply || selectedHouse.properties.cold_water_supply, "cold_water_supply", "Тип системы холодного водоснабжения")}</div>
                  <div className="info-item"><b>Материал системы холодного водоснабжения:</b> {renderFieldData(selectedBuildingDetails?.properties?.cold_water_system_material || selectedHouse.properties.cold_water_system_material, "cold_water_system_material", "Материал системы холодного водоснабжения")}</div>
                  <div className="info-item"><b>Физический износ системы холодного водоснабжения (%):</b> {renderFieldData(selectedBuildingDetails?.properties?.cold_water_system_wear || selectedHouse.properties.cold_water_system_wear, "cold_water_system_wear", "Физический износ системы холодного водоснабжения (%)")}</div>
                  <div className="info-item"><b>Физический износ стояков холодного водоснабжения (%):</b> {renderFieldData(selectedBuildingDetails?.properties?.cold_water_riser_wear || selectedHouse.properties.cold_water_riser_wear, "cold_water_riser_wear", "Физический износ стояков холодного водоснабжения (%)")}</div>
              </details>

              <details>
                  <summary>Система электроснабжения</summary>
                  <div className="info-item"><b>Тип системы электроснабжения:</b> {renderFieldData(selectedBuildingDetails?.properties?.electricity_supply || selectedHouse.properties.electricity_supply, "electricity_supply", "Тип системы электроснабжения")}</div>
                  <div className="info-item"><b>Физический износ системы электроснабжения (%):</b> {renderFieldData(selectedBuildingDetails?.properties?.electricity_system_wear || selectedHouse.properties.electricity_system_wear, "electricity_system_wear", "Физический износ системы электроснабжения (%)")}</div>
                  <div className="info-item"><b>Количество вводов в здание:</b> {renderFieldData(selectedBuildingDetails?.properties?.electricity_inputs_count || selectedHouse.properties.electricity_inputs_count, "electricity_inputs_count", "Количество вводов в здание")}</div>
                  <div className="info-item"><b>Год последнего капитального ремонта системы электроснабжения:</b> {renderFieldData(selectedBuildingDetails?.properties?.electricity_last_capital_repair_year || selectedHouse.properties.electricity_last_capital_repair_year, "electricity_last_capital_repair_year", "Год последнего капитального ремонта системы электроснабжения")}</div>
              </details>

              <details>
                  <summary>Стояки системы горячего водоснабжения</summary>
                  <div className="info-item"><b>Материал стояков ГВС:</b> {renderFieldData(selectedBuildingDetails?.properties?.hot_water_risers_material || selectedHouse.properties.hot_water_risers_material, "hot_water_risers_material", "Материал стояков ГВС")}</div>
                  <div className="info-item"><b>Физический износ стояков ГВС (%):</b> {renderFieldData(selectedBuildingDetails?.properties?.hot_water_riser_wear || selectedHouse.properties.hot_water_riser_wear, "hot_water_riser_wear", "Физический износ стояков ГВС (%)")}</div>
              </details>

              <details>
                  <summary>Стояки системы отопления</summary>
                  <div className="info-item"><b>Физический износ стояков системы отопления (%):</b> {renderFieldData(selectedBuildingDetails?.properties?.heating_riser_wear || selectedHouse.properties.heating_riser_wear, "heating_riser_wear", "Физический износ стояков системы отопления (%)")}</div>
              </details>

              <details>
                  <summary>Стояки системы холодного водоснабжения</summary>
                  <div className="info-item"><b>Физический износ стояков системы холодного водоснабжения (%):</b> {renderFieldData(selectedBuildingDetails?.properties?.cold_water_riser_wear || selectedHouse.properties.cold_water_riser_wear, "cold_water_riser_wear", "Физический износ стояков системы холодного водоснабжения (%)")}</div>
              </details>

              <details>
                  <summary>Фасад</summary>
                  <div className="info-item"><b>Тип наружных стен:</b> {renderFieldData(selectedBuildingDetails?.properties?.facade_wall_type || selectedHouse.properties.facade_wall_type, "facade_wall_type", "Тип наружных стен")}</div>
                  <div className="info-item"><b>Тип утепления фасада:</b> {renderFieldData(selectedBuildingDetails?.properties?.facade_insulation_type || selectedHouse.properties.facade_insulation_type, "facade_insulation_type", "Тип утепления фасада")}</div>
                  <div className="info-item"><b>Материал отделки фасада:</b> {renderFieldData(selectedBuildingDetails?.properties?.facade_material || selectedHouse.properties.facade_material, "facade_material", "Материал отделки фасада")}</div>
                  <div className="info-item"><b>Физический износ фасада (%):</b> {renderFieldData(selectedBuildingDetails?.properties?.facade_wear || selectedHouse.properties.facade_wear, "facade_wear", "Физический износ фасада (%)")}</div>
                  <div className="info-item"><b>Год последнего капитального ремонта фасада:</b> {renderFieldData(selectedBuildingDetails?.properties?.facade_last_capital_repair_year || selectedHouse.properties.facade_last_capital_repair_year, "facade_last_capital_repair_year", "Год последнего капитального ремонта фасада")}</div>
              </details>

              <details>
                  <summary>Фундамент</summary>
                  <div className="info-item"><b>Тип фундамента:</b> {renderFieldData(selectedBuildingDetails?.properties?.foundation_type || selectedHouse.properties.foundation_type, "foundation_type", "Тип фундамента")}</div>
                  <div className="info-item"><b>Материал фундамента:</b> {renderFieldData(selectedBuildingDetails?.properties?.foundation_material || selectedHouse.properties.foundation_material, "foundation_material", "Материал фундамента")}</div>
                  <div className="info-item"><b>Площадь фундамента (м²):</b> {renderFieldData(selectedBuildingDetails?.properties?.foundation_area || selectedHouse.properties.foundation_area, "foundation_area", "Площадь фундамента (м²)")}</div>
                  <div className="info-item"><b>Физический износ фундамента (%):</b> {renderFieldData(selectedBuildingDetails?.properties?.foundation_wear || selectedHouse.properties.foundation_wear, "foundation_wear", "Физический износ фундамента (%)")}</div>
                  <div className="info-item"><b>Год последнего капитального ремонта фундамента:</b> {renderFieldData(selectedBuildingDetails?.properties?.foundation_last_capital_repair_year || selectedHouse.properties.foundation_last_capital_repair_year, "foundation_last_capital_repair_year", "Год последнего капитального ремонта фундамента")}</div>
              </details>

              <details>
                  <summary>Юридическая информация</summary>
                  <div className="info-item"><b>Кадастровый номер:</b> {renderFieldData(selectedBuildingDetails?.properties?.cadastral_number || selectedHouse.properties.cadastral_number, "cadastral_number", "Кадастровый номер")}</div>
                  <div className="info-item"><b>Код ОКТМО:</b> {renderFieldData(selectedBuildingDetails?.properties?.oktmo_code || selectedHouse.properties.oktmo_code, "oktmo_code", "Код ОКТМО")}</div>
                  <div className="info-item"><b>Почтовый индекс:</b> {renderFieldData(selectedBuildingDetails?.properties?.postal_code || selectedHouse.properties.postal_code, "postal_code", "Почтовый индекс")}</div>
                  <div className="info-item"><b>Управляющая компания:</b> {renderFieldData(selectedBuildingDetails?.properties?.management_company || selectedHouse.properties.management_company, "management_company", "Управляющая компания")}</div>
                  <div className="info-item"><b>Фонд капитального ремонта:</b> {renderFieldData(selectedBuildingDetails?.properties?.capital_repair_fund || selectedHouse.properties.capital_repair_fund, "capital_repair_fund", "Фонд капитального ремонта")}</div>
                  <div className="info-item"><b>Объект культурного наследия:</b> {renderFieldData(selectedBuildingDetails?.properties?.cultural_heritage_status || selectedHouse.properties.cultural_heritage_status, "cultural_heritage_status", "Объект культурного наследия")}</div>
                  <div className="info-item"><b>Дом снесён:</b> {renderFieldData(selectedBuildingDetails?.properties?.is_demolished || selectedHouse.properties.is_demolished, "is_demolished", "Дом снесён")}</div>
              </details>

              <details>
                <summary>График физического износа</summary>
                <div id="chart-container">
                  <GraphChart
                    chartMode="wear"
                    yearBuilt={selectedBuildingDetails?.properties?.year_built || selectedHouse.properties.year_built}
                    series={selectedBuildingDetails?.properties?.series || selectedHouse.properties.series}
                    inline={true}
                  />
                  <button 
                    className="chart-expand-button" 
                    onClick={() => setFullscreenChartVisible(true)}
                    title="Развернуть график"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 3H21V9" stroke="#1bc298" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 21H3V15" stroke="#1bc298" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M21 3L14 10" stroke="#1bc298" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 21L10 14" stroke="#1bc298" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button className="expand-chart-btn" onClick={() => setFullscreenChartVisible(true)}>
                    Развернуть график
                  </button>
                </div>
              </details>
              <details>
                <summary>График морального износа</summary>
                <div id="moral-chart-container">
                  <GraphChart
                    chartMode="moral"
                    yearBuilt={selectedBuildingDetails?.properties?.year_built || selectedHouse.properties.year_built}
                    series={selectedBuildingDetails?.properties?.series || selectedHouse.properties.series}
                    inline={true}
                  />
                  <button 
                    className="chart-expand-button" 
                    onClick={() => setFullscreenMoralChartVisible(true)}
                    title="Развернуть график"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 3H21V9" stroke="#1bc298" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 21H3V15" stroke="#1bc298" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M21 3L14 10" stroke="#1bc298" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 21L10 14" stroke="#1bc298" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button className="expand-chart-btn" onClick={() => setFullscreenMoralChartVisible(true)}>
                    Развернуть график
                  </button>
                </div>
              </details>
              {userRole > 1 && (
                <a
                  className="download-report-btn"
                  href={`${API_URL}/Report/house-report-doc/${selectedBuildingDetails?.properties?.id || selectedHouse.properties.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Скачать отчет
                </a>
              )}
            </div>
          </div>
        )}
        {fullscreenChartVisible && (
          <FullscreenChart
            visible={fullscreenChartVisible}
            onClose={() => setFullscreenChartVisible(false)}
            chartMode="wear"
            yearBuilt={selectedBuildingDetails?.properties?.year_built || selectedHouse.properties.year_built}
            series={selectedBuildingDetails?.properties?.series || selectedHouse.properties.series}
          />
        )}
        {fullscreenMoralChartVisible && (
          <FullscreenChart
            visible={fullscreenMoralChartVisible}
            onClose={() => setFullscreenMoralChartVisible(false)}
            chartMode="moral"
            yearBuilt={selectedBuildingDetails?.properties?.year_built || selectedHouse.properties.year_built}
            series={selectedBuildingDetails?.properties?.series || selectedHouse.properties.series}
          />
        )}
      </div>

      {/* Модальное окно для редактирования поля */}
      {isEditModalOpen && (
        <div className="edit-field-modal-overlay">
          <div className={`edit-field-modal ${isDarkMode ? 'info-panel-dark' : 'info-panel-light'}`}>
            <div className="edit-field-modal-header">
              <h3>Редактирование: {editingLabel}</h3>
              <button className="close-modal-button" onClick={closeEditModal}>✖</button>
            </div>
            
            <div className="edit-field-modal-body">
              <form className="edit-field-form" onSubmit={(e) => { e.preventDefault(); saveEditedField(); }}>
                <label htmlFor="field-value">Новое значение:</label>
                
                {getFieldType(editingField) === "boolean" ? (
                  <div className="radio-buttons-container">
                    <label className="radio-button">
                      <input 
                        type="radio" 
                        name="boolean-value" 
                        value="true" 
                        checked={editingValue === "true"}
                        onChange={() => setEditingValue("true")}
                      />
                      <span>Да</span>
                    </label>
                    <label className="radio-button">
                      <input 
                        type="radio" 
                        name="boolean-value" 
                        value="false" 
                        checked={editingValue === "false"}
                        onChange={() => setEditingValue("false")}
                      />
                      <span>Нет</span>
                    </label>
                  </div>
                ) : getFieldType(editingField) === "wear" ? (
                  <div className="number-input-container">
                    <input 
                      id="field-value"
                      type="number" 
                      min="0" 
                      max="100" 
                      step="0.1"
                      value={editingValue} 
                      onChange={(e) => {
                        setEditingValue(e.target.value);
                        // Сбрасываем ошибку валидации при изменении значения
                        setValidationError(null);
                      }}
                      autoFocus
                      autoComplete="off"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          saveEditedField();
                        } else if (e.key === 'Escape') {
                          closeEditModal();
                        }
                      }}
                    />
                    <span className="input-suffix">%</span>
                  </div>
                ) : getFieldType(editingField) === "numeric" ? (
                  <input 
                    id="field-value"
                    type="number" 
                    value={editingValue} 
                    onChange={(e) => {
                      setEditingValue(e.target.value);
                      setValidationError(null);
                    }}
                    autoFocus
                    autoComplete="off"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        saveEditedField();
                      } else if (e.key === 'Escape') {
                        closeEditModal();
                      }
                    }}
                  />
                ) : (
                  <input 
                    id="field-value"
                    type="text" 
                    value={editingValue} 
                    onChange={(e) => {
                      setEditingValue(e.target.value);
                      setValidationError(null);
                    }}
                    autoFocus
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck="false"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        saveEditedField();
                      } else if (e.key === 'Escape') {
                        closeEditModal();
                      }
                    }}
                  />
                )}
                
                {validationError && (
                  <div className="validation-error">
                    {validationError}
                  </div>
                )}
                
                {editResult && (
                  <div className={`edit-result ${editResult.type}`}>
                    {editResult.type === 'loading' && <div className="loading-spinner"></div>}
                    {editResult.message}
                  </div>
                )}
              </form>
            </div>
            
            <div className="edit-field-modal-footer">
              <button className="cancel-button" onClick={closeEditModal}>Отмена</button>
              <button 
                className="save-button" 
                onClick={saveEditedField} 
                disabled={editResult?.type === 'loading' || validationError !== null}
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default MapSection;
